/**
 * Documentos tributarios del comité (Console, DTE Fase C.1). Los datos del
 * emisor tal como los tiene el SII, la sucursal de SimpleFactura y el
 * ambiente. El interruptor parte apagado: mientras no se habilite, las
 * boletas siguen sin folio SII. Las credenciales de la cuenta Partner no se
 * editan acá (viven en Render).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileCheck2, Loader2 } from "lucide-react";

import { useToast } from "../../components/Toast";
import { Badge, Card, Cargando, ErrorCarga, Input } from "../../components/ui";
import { mensajeDeError } from "../../lib/api";
import {
  fetchConfigDTE,
  guardarConfigDTE,
  type ConfiguracionDTE,
  type ConfiguracionDTEUpdate,
} from "./api";

type Form = Omit<ConfiguracionDTEUpdate, "acteco"> & { acteco: string };

function aForm(c: ConfiguracionDTE): Form {
  return {
    habilitado: c.habilitado,
    produccion: c.produccion,
    sucursal: c.sucursal,
    razon_social: c.razon_social,
    giro: c.giro,
    direccion: c.direccion,
    comuna: c.comuna,
    acteco: c.acteco === null ? "" : String(c.acteco),
  };
}

function estado(c: ConfiguracionDTE): { texto: string; tono: "emerald" | "amber" | "slate" } {
  if (!c.habilitado) return { texto: "Apagado", tono: "slate" };
  if (c.produccion) return { texto: "Emitiendo en producción", tono: "emerald" };
  return { texto: "Habilitado en certificación", tono: "amber" };
}

export function DteEmpresaCard({ empresaId }: { empresaId: number }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const clave = ["superadmin", "dte", empresaId];
  const q = useQuery({ queryKey: clave, queryFn: () => fetchConfigDTE(empresaId) });
  const [form, setForm] = useState<Form | null>(null);
  useEffect(() => {
    if (q.data) setForm(aForm(q.data));
  }, [q.data]);

  const mut = useMutation({
    mutationFn: (f: Form) =>
      guardarConfigDTE(empresaId, {
        ...f,
        acteco: f.acteco.trim() === "" ? null : Number(f.acteco),
      }),
    onSuccess: (r) => {
      queryClient.setQueryData(clave, r);
      toast.success(
        r.habilitado
          ? `Emisión habilitada en ${r.produccion ? "producción" : "certificación"}.`
          : "Configuración guardada. La emisión sigue apagada.",
      );
    },
    onError: (err) => toast.error(mensajeDeError(err, "No se pudo guardar la configuración")),
  });

  const set = <K extends keyof Form>(campo: K, valor: Form[K]): void =>
    setForm((f) => (f ? { ...f, [campo]: valor } : f));
  const cambiado =
    form !== null &&
    q.data !== undefined &&
    JSON.stringify(form) !== JSON.stringify(aForm(q.data));

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
          <FileCheck2 className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
          Documentos tributarios (SII)
        </h2>
        {q.data && <Badge tone={estado(q.data).tono}>{estado(q.data).texto}</Badge>}
      </div>

      {q.isError ? (
        <ErrorCarga error={q.error} />
      ) : q.isLoading || !form ? (
        <Cargando inline />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mut.mutate(form);
          }}
          className="space-y-4"
        >
          <p className="text-sm text-neutral-600">
            Emisor <strong>{q.data?.rut_emisor}</strong>. Los datos deben ser los que el SII
            tiene registrados para el comité.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Razón social"
              required
              value={form.razon_social}
              onChange={(e) => set("razon_social", e.target.value)}
              containerClassName="sm:col-span-2"
            />
            <Input label="Giro" value={form.giro} onChange={(e) => set("giro", e.target.value)} />
            <Input
              label="Código de actividad (acteco)"
              inputMode="numeric"
              value={form.acteco}
              onChange={(e) => set("acteco", e.target.value.replace(/\D/g, ""))}
              hint="Agua potable: 360000"
            />
            <Input
              label="Dirección"
              value={form.direccion}
              onChange={(e) => set("direccion", e.target.value)}
            />
            <Input
              label="Comuna"
              value={form.comuna}
              onChange={(e) => set("comuna", e.target.value)}
            />
            <Input
              label="Sucursal en SimpleFactura"
              value={form.sucursal}
              onChange={(e) => set("sucursal", e.target.value)}
            />
          </div>

          <div className="space-y-2 rounded-md border border-neutral-200 p-3">
            <label className="flex items-start gap-2 text-sm text-neutral-800">
              <input
                type="checkbox"
                checked={form.habilitado}
                onChange={(e) => set("habilitado", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-neutral-450 text-brand-700"
              />
              <span>
                <strong>Habilitar la emisión</strong>
                <span className="block text-xs text-neutral-600">
                  Exige razón social, giro, dirección, comuna y sucursal.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-neutral-800">
              <input
                type="checkbox"
                checked={form.produccion}
                onChange={(e) => set("produccion", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-neutral-450 text-brand-700"
              />
              <span>
                <strong>Ambiente de producción</strong>
                <span className="block text-xs text-neutral-600">
                  Sin marcar se emite en certificación (sin valor tributario). Marcar sólo
                  después del corte con el proveedor anterior.
                </span>
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={!cambiado || mut.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar
          </button>
        </form>
      )}
    </Card>
  );
}
